import React from 'react';
import PropTypes from 'prop-types';
import { Label, Dropdown, Loader } from '@gasbuddy/react-components';

import Notification from '../notification';
import { StationListType } from '../../../lib/constants/enums';
import styles from './styles.css';

const {
  api,
  getMostRecentStationListId,
  setMostRecentStationListId,
  getQueryParameters,
  setQueryParameter,
} = require('../../../lib/bputils').default;

export default function StationListSelector({
  label,
  onChange,
  stationLists: stationListProps,
  storeSelectedList,
  createStationListOption,
  searchable,
}) {
  const [stationListId, setStationListId] = React.useState(null);
  const [stationLists, setStationLists] = React.useState(stationListProps || []);
  const [stationListOptions, setStationListOptions] = React.useState([]);

  React.useEffect(() => {
    const listId = getListId();
    if (listId) {
      setStationListId(setStationListId);
    }
    if (!stationLists.length) {
      setStationListOptions([generateLoadingOption()]);
      getStationLists();
    } else {
      generateStationOptions(stationLists);
    }
  }, []);

  React.useEffect(() => {
    if (stationLists.length && !stationListId) {
      setStationListId(getAllStationsListId());
    }
  }, [stationLists, stationListId]);

  React.useEffect(() => {
    if (stationListId) {
      onChange(stationListId);
      if (storeSelectedList) {
        setMostRecentStationListId(stationListId);
        setQueryParameter('stationListId', stationListId);
      }
    }
  }, [stationListId]);

  const generateLoadingOption = () => ({
    value: 0,
    label: 'Loading',
    render: () => <Loader>Fetching station lists ...</Loader>,
  });

  const handleOnCreateStationListClick = () => {
    localStorage.createList = true;
    window.location = '/client/station-profiles/station-list.html';
  };

  const generateCreateNewStationListOption = () => [{
    value: -1,
    label: '',
    render: () => (
      <div className={styles.createNewStationList} onClick={handleOnCreateStationListClick} aria-hidden="true">
        Create new station list
      </div>
    ),
  }];

  const getListId = () => {
    let listId = null;
    const queryParams = getQueryParameters();
    listId = queryParams?.stationListId;
    if (!listId) {
      listId = getMostRecentStationListId();
    }
    return listId ? Number(listId) : listId;
  };

  // every organization has an "all stations" list, so arr.find() will never be null
  const getAllStationsListId = () => stationLists.find(sl => sl.list_type === StationListType.AllStations).id;

  const getStationLists = () => {
    api.get({
      requestId: 'getAllStationLists',
      path: '/station-lists',
      success: (res) => {
        setStationLists(res);
        generateStationOptions(res);
      },
      error: () => {
        Notification.error('Unable to fetch your station lists');
      },
    });
  };

  const renderStationListOptions = (listArr = []) => listArr.map(sl => ({
    value: sl.id,
    label: `${sl.name} (${sl.station_count})`,
  }));

  const generateStationOptions = (listArr) => {
    setStationListOptions([
      ...(createStationListOption ? generateCreateNewStationListOption() : []),
      ...renderStationListOptions(listArr.filter(l => l.list_type === StationListType.AllStations)),
      ...renderStationListOptions(listArr.filter(l => l.list_type === StationListType.Watched)),
      ...renderStationListOptions(listArr.filter(l => l.list_type === StationListType.Shared)),
      ...renderStationListOptions(listArr.filter(l => l.list_type === StationListType.Private)),
    ]);
  };

  const handleOnStationListChange = (e) => {
    const listId = Number(e.target.value);
    if (listId && listId !== stationListId) {
      setStationListId(listId);
    }
  };

  return (
    <Label htmlFor="stationListDropdown" className={styles.labelText}>
      { label && label}
      <Dropdown
        id="stationListDropdown"
        animated
        bordered
        options={stationListOptions}
        onChange={handleOnStationListChange}
        value={stationListId}
        placeholder={(stationLists.length && stationListId) ? 'Select a Station List' : 'Loading...'}
        searchable={searchable}
      />
    </Label>
  );
}

StationListSelector.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  stationLists: PropTypes.arrayOf(PropTypes.object),
  storeSelectedList: PropTypes.bool,
  createStationListOption: PropTypes.bool,
  searchable: PropTypes.bool,
};

StationListSelector.defaultProps = {
  label: undefined,
  onChange: () => { },
  stationLists: [],
  storeSelectedList: false,
  createStationListOption: false,
  searchable: false,
};
